import AdviserProfilesSearchApi from '../../core/api/AdviserProfilesSearchApi'
import { fetchPublishedProfile } from '../../core/actions/adviserProfiles'
import history from '../../core/history'
import { actions as searchCriteriaActions } from '../FindAnIndependantFinancialAdviser/searchCriteriaActions'

export const events = {
  filter: {
    profiles: 'SEARCH_RESULT_PROFILE_FILTER'
  },
  set: {
    searchBar: {
      ifaName: 'SET_SEARCHBAR_IFANAME'
    },
    searchResults: {
      currentPage: 'SET_SEARCHRESULTS_CURRENTPAGE'
    },
    resetFilter: 'RESET_FILTER'
  },
  clear: {
    searchBar: {
      ifaName: 'SET_SEARCHBAR_IFANAME_TONOTHING'
    },
    results: 'CLEAR_SEARCH_RESULTS'
  },
  fetching: {
    nextPage: 'FETCHING_NEXT_PAGE',
    searchResults: 'FETCHING_SEARCH_RESULTS'
  },
  fetched: {
    searchResults: 'FETCHED_SEARCH_RESULTS'
  },
  sort: {
    byRatingAscending: 'SORT_BY_RATING_ASCENDING',
    byRatingDescending: 'SORT_BY_RATING_DESCENDING',
    byDistanceAscending: 'SORT_BY_DISTANCE_ASCENDING',
    byDistanceDescending: 'SORT_BY_DISTANCE_DESCENDING'
  }
}

const fetchingSearchResults = () => ({
  type: events.fetching.searchResults
})

const fetchingNextPage = () => ({
  type: events.fetching.nextPage
})

const adviserProfilesSearchApi = new AdviserProfilesSearchApi()

const jsonDeserialized = (json, pageSize) => ({
  type: events.fetched.searchResults,
  payload: Array.isArray(json) ? json : [json],
  pageSize
})

export function fetchNextPage (profileIds, currentPage, pageSize) {
  return (dispatch) => {
    if (Math.ceil(profileIds.length / pageSize) >= currentPage + 1) {
      fetchProfiles(profileIds, dispatch, currentPage + 1, pageSize)
      dispatch(fetchPublishedProfilesForPage(profileIds, currentPage + 1, pageSize))
      dispatch(fetchingNextPage())
    }
  }
}

export function fetchPage (profileIds, page, pageSize) {
  return (dispatch) => {
    if (Math.ceil(profileIds.length / pageSize) >= page) {
      // Update currentPage so the reducer rebuilds the slice for the clicked page.
      // Without this, the reducer rebuilds the SAME (unchanged) currentPage, so the
      // Pager click appeared to do nothing.
      dispatch(setCurrentPage(page))
      fetchProfiles(profileIds, dispatch, page, pageSize)
      dispatch(fetchPublishedProfilesForPage(profileIds, page, pageSize))
    }
  }
}

function fetchAllProfiles (searchResults, currentPage, pageSize) {
  return (dispatch) => {
    var page = currentPage
    var hasMorePages = currentPage * pageSize <= searchResults.length ||
    currentPage * pageSize % searchResults < pageSize
    while (hasMorePages) {
      dispatch(fetchNextPage(searchResults, page, pageSize))
      hasMorePages = ++page * pageSize <= searchResults.length ||
      page * pageSize % searchResults < pageSize
    }
  }
}

function fetchProfiles (searchResults, dispatch, page, pageSize) {
  var startIndex = page * pageSize - pageSize
  var endIndex = Math.min(page * pageSize, searchResults.length)
  var pageProfiles = searchResults.slice(startIndex, endIndex)
  dispatch({
    type: events.filter.profiles,
    allProfiles: pageProfiles
  })
}

// Fetch the published profiles for a single page slice so navigating to a later page loads
// that page's profiles (handleResponse no longer eagerly loads all ~1000 up front).
function fetchPublishedProfilesForPage (profileIds, page, pageSize) {
  return (dispatch) => {
    const startIndex = page * pageSize - pageSize
    const endIndex = Math.min(page * pageSize, profileIds.length)
    profileIds.slice(startIndex, endIndex).forEach(item => {
      const adviserId = item && item.adviserId ? item.adviserId : item
      if (adviserId) {
        dispatch(fetchPublishedProfile(adviserId))
      }
    })
  }
}

const handleResponse = async (dispatch, getState, response, currentPage, pageSize) => {
  try {
    const json = await response
    const normalized = Array.isArray(json) ? json : [json]

    dispatch(jsonDeserialized(normalized, pageSize))

    // Hydrate from the reducer's sorted id list, not the raw response order. The reducer sorts
    // profileIds on load, so slicing the raw order fetched a different set of advisers than the
    // page actually renders, leaving the first page short until you navigated pages.
    // Only the CURRENT page is fetched - eagerly fetching all (~1000) froze the browser.
    const sortedProfileIds = getState().searchResults.profileIds
    fetchProfiles(sortedProfileIds, dispatch, currentPage, pageSize)
    dispatch(fetchPublishedProfilesForPage(sortedProfileIds, currentPage, pageSize))
  } catch (err) {
    console.error('Error parsing search results: ', err)
  }
}

function shouldUseUrlParams (searchCriteria) {
  const storeParams = searchCriteria.store
  const urlParams = searchCriteria.url
  if (!storeParams.selectedInvestorDescriptionId) {
    return true
  }
  var useUrlParams = (urlParams.languagePreferences.length !== 0 && storeParams.selectedPreferredLanguageIds.length === 0) ||
    (urlParams.investmentAmounts.onceOffAmount !== undefined && storeParams.investmentAmounts.onceOffAmount === undefined) ||
    (urlParams.investmentAmounts.monthlyAmount !== undefined && storeParams.investmentAmounts.monthlyAmount === undefined) ||
    (urlParams.userAddress !== undefined && storeParams.userAddress === undefined || storeParams.userAddress === '') ||
    (storeParams.financialServices.length === 0 && urlParams.financialServices.length !== 0)
  return useUrlParams
}

function getSearchParams (searchCriteria) {
  return shouldUseUrlParams(searchCriteria) ? searchCriteria.url : searchCriteria.store
}

function fetchSearchResults (currentPage, pageSize, searchCriteria, resetSortBy = false) {
  const searchParams = getSearchParams(searchCriteria)
  const request = adviserProfilesSearchApi.searchPublished(searchParams)
  var resultsRefreshed = searchCriteriaActions.set.resultsRefreshed

  return (dispatch, getState) => {
    if (resetSortBy) {
      dispatch(resetFilter())
    }
    dispatch(fetchingSearchResults())
    request.then(async function (response) {
      dispatch({ type: resultsRefreshed })
      await handleResponse(dispatch, getState, response, currentPage, pageSize)
    })
  }
}

const setCurrentPage = (page) => ({
  type: events.set.searchResults.currentPage,
  page
})

const resetFilter = () => ({
  type: events.set.resetFilter
})

const setIfaName = (ifaName) => ({
  type: events.set.searchBar.ifaName,
  ifaName
})

const clearIfaName = () => ({
  type: events.clear.searchBar.ifaName
})

function sanitizeIfaName (searchInput) {
  searchInput = searchInput.split("'").join("''")
  searchInput = Buffer.from(searchInput).toString('base64')
  return searchInput
}

export const actions = {
  filter: {
    profiles: (allProfiles) => ({ type: events.filter.profiles, allProfiles })
  },
  fetch: {
    page: fetchPage,
    allPages: fetchAllProfiles,
    nextPage: function (profileIds, currentPage, pageSize) {
      return (dispatch) => {
        if (currentPage * pageSize < profileIds.length - 1) {
          fetchProfiles(profileIds, dispatch, currentPage + 1, pageSize)
          dispatch(fetchingNextPage())
        }
      }
    },
    findIfaByNameResults: function (ifaName, preventNavigation, pageSize) {
      var sanitizedIfaName = sanitizeIfaName(ifaName)
      const request = adviserProfilesSearchApi.searchPublishedProfilesByName(ifaName)
      var resultsRefreshed = searchCriteriaActions.set.resultsRefreshed
      return (dispatch, getState) => {
        dispatch({ type: events.clear.results })
        dispatch(fetchingSearchResults())
        // Navigate to the results screen up front so its spinner shows while the (possibly
        // large) result set loads, instead of freezing on the Find-an-IFA screen.
        if (!preventNavigation) {
          history.push(`/search-results/search=${sanitizedIfaName}`)
        }
        request.then(async function (response) {
          const json = response.data ? response.data : await response
          await handleResponse(dispatch, getState, json, 1, pageSize)
          dispatch(clearIfaName())
          dispatch({ type: resultsRefreshed })
        })
      }
    },
    profiles: fetchProfiles,
    searchResults: fetchSearchResults
  },
  set: {
    ifaName: setIfaName,
    resultsRefreshed: () => ({
      type: searchCriteriaActions.set.resultsRefreshed,
      resultsRefreshed: true,
      dontValid: true
    }),
    resetFilter: resetFilter
  },
  clear: {
    ifaName: clearIfaName,
    results: events.clear.results
  },
  sort: {
    byRatingDescending: () => (dispatch, getState) => {
      dispatch({ type: events.sort.byRatingDescending })
      const updatedState = getState().searchResults
      return fetchPage(updatedState.profileIds, updatedState.currentPage, updatedState.pageSize)(dispatch)
    },
    byDistanceAscending: () => (dispatch, getState) => {
      dispatch({ type: events.sort.byDistanceAscending })
      const updatedState = getState().searchResults
      return fetchPage(updatedState.profileIds, updatedState.currentPage, updatedState.pageSize)(dispatch)
    }
  }
}
